import type { AppSettings, EnvironmentMode, MenuItem, MenuItemDraft, RequestStatus, SalonRequest, SubmitRequestInput } from '../types'

interface ApiErrorBody { error?: { message?: string } }

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...init?.headers },
  })
  const body = await response.json().catch(() => ({})) as T & ApiErrorBody
  if (!response.ok) throw new Error(body.error?.message || 'The request could not be completed.')
  return body
}

export const fetchPublicMenu = () => api<{ menu: MenuItem[]; settings: Pick<AppSettings, 'pauseNonessential'> }>('/api/v1/public/menu')

export const submitApiRequest = (input: SubmitRequestInput) =>
  api<{ request: Omit<SalonRequest, 'sessionId' | 'idempotencyKey'> }>('/api/v1/public/requests', { method: 'POST', body: JSON.stringify(input) })

export const fetchRequestStatus = (id: string) =>
  api<{ request: Omit<SalonRequest, 'sessionId' | 'idempotencyKey'> }>(`/api/v1/public/requests/${encodeURIComponent(id)}`)

export const fetchStaffBootstrap = () => api<{ menu: MenuItem[]; requests: Array<Omit<SalonRequest, 'sessionId' | 'idempotencyKey'>>; settings: AppSettings; environment: { mode: EnvironmentMode } }>('/api/v1/staff/bootstrap')

export const patchApiStatus = (id: string, status: RequestStatus) =>
  api<{ ok: true }>(`/api/v1/staff/requests/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status }) })

export const createApiMenuItem = (item: MenuItemDraft) =>
  api<{ item: MenuItem }>('/api/v1/staff/menu', { method: 'POST', body: JSON.stringify(item) })

export const patchApiMenuItem = (id: string, patch: Partial<MenuItemDraft & Pick<MenuItem, 'archived'>>) =>
  api<{ item: MenuItem }>(`/api/v1/staff/menu/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) })

export const archiveApiMenuItem = (id: string) =>
  api<{ item: MenuItem }>(`/api/v1/staff/menu/${encodeURIComponent(id)}`, { method: 'DELETE' })

export const patchApiSettings = (patch: Partial<AppSettings>) =>
  api<{ ok: true }>('/api/v1/staff/settings', { method: 'PATCH', body: JSON.stringify(patch) })
